import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, MailCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { emit } from "@/lib/events";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Jammu & Kashmir","Ladakh","Puducherry","Chandigarh","Andaman & Nicobar"];

// Country rules mirror backend otp_service.COUNTRY_RULES (validation is server-side too)
const COUNTRIES = [
  { code: "+91", label: "IN +91", len: 10, re: /^[6-9]\d{9}$/ },
  { code: "+1", label: "US/CA +1", len: 10, re: /^\d{10}$/ },
  { code: "+44", label: "UK +44", len: 10, re: /^\d{10}$/ },
  { code: "+971", label: "UAE +971", len: 9, re: /^\d{9}$/ },
  { code: "+65", label: "SG +65", len: 8, re: /^\d{8}$/ },
  { code: "+61", label: "AU +61", len: 9, re: /^\d{9}$/ },
];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, coupon, setCoupon, clear, hasPersonalised, refreshPrices } = useCart();
  const hasMto = items.some((i) => i.tier === "made_to_order");
  const mtoLead = items.find((i) => i.tier === "made_to_order")?.lead_time || "";
  const { user, otpLogin, logout } = useAuth();
  const { config, offers } = useStore();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", phone: "", countryCode: "+91", pincode: "", line1: "", line2: "", city: "", state: "" });
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [code, setCode] = useState(coupon?.code || "");
  const [placing, setPlacing] = useState(false);
  const [mockPay, setMockPay] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [gift, setGift] = useState({ wrap: false, message: "", hide_price: false });
  const [priceChanges, setPriceChanges] = useState(null); // 409 diff

  // Email-first OTP step state
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpName, setOtpName] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [devOtp, setDevOtp] = useState(""); // stub-mode only: surfaced so testers can complete flows
  const [resendIn, setResendIn] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [pinLookup, setPinLookup] = useState(""); // "" | "loading" | "ok" | "fail"
  const [exitOffer, setExitOffer] = useState(null); // exit-intent offer dialog
  const [wallet, setWallet] = useState(null);       // {balance} when logged in
  const [loyalty, setLoyalty] = useState(null);     // {balance, ratio...} when logged in
  const [useWallet, setUseWallet] = useState(false);
  const [useLoyalty, setUseLoyalty] = useState(false);
  const begunRef = useRef(false);

  // Exit-intent: leaving the page upward with items in cart → one-shot offer nudge
  useEffect(() => {
    if (!items.length) return;
    if (sessionStorage.getItem("siyaara_exit_offer_seen")) return;
    const onLeave = (e) => {
      if (e.clientY > 8 || sessionStorage.getItem("siyaara_exit_offer_seen")) return;
      sessionStorage.setItem("siyaara_exit_offer_seen", "1");
      const best = (offers || [])[0];
      if (best) setExitOffer(best);
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [items.length, offers]);

  // Resend countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    if (begunRef.current || !items.length) return;
    begunRef.current = true;
    emit("begin_checkout", {
      value: subtotal,
      items: items.map((i) => ({
        item_id: i.variant_id || i.product_id,
        item_name: i.title || i.name || "",
        price: i.locked_price,
        quantity: i.qty,
      })),
    }, { email: user?.email, phone: user?.phone });
  }, [items, subtotal, user]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        phone: f.phone || (user.phone && !user.phone.includes("@") ? user.phone.replace(/^\+\d{1,3}/, "") : ""),
      }));
      api.get("/auth/addresses").then((r) => {
        setAddresses(r.data);
        const def = r.data.find((a) => a.is_default) || r.data[0];
        if (def) setForm((f) => ({ ...f, ...pick(def) }));
      }).catch(() => {});
    }
  }, [user]);

  // Wallet + loyalty balances once signed in
  useEffect(() => {
    if (!user) { setWallet(null); setLoyalty(null); return; }
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/loyalty").then((r) => setLoyalty(r.data)).catch(() => {});
  }, [user]);

  // Pincode → city/state autofill
  useEffect(() => {
    if (!/^\d{6}$/.test(form.pincode)) { setPinLookup(""); return; }
    let cancelled = false;
    setPinLookup("loading");
    api.get(`/pincode/${form.pincode}`).then((r) => {
      if (cancelled) return;
      setPinLookup("ok");
      setForm((f) => ({ ...f, city: f.city || r.data.city, state: f.state || r.data.state }));
    }).catch(() => { if (!cancelled) setPinLookup("fail"); });
    return () => { cancelled = true; };
  }, [form.pincode]);

  const pick = (a) => ({ pincode: a.pincode, line1: a.line1, line2: a.line2, city: a.city, state: a.state });

  const country = COUNTRIES.find((c) => c.code === form.countryCode) || COUNTRIES[0];
  const phoneDigits = form.phone.replace(/\D/g, "").slice(0, country.len);
  const phoneValid = country.re.test(phoneDigits);
  const phoneE164 = `${form.countryCode}${phoneDigits.replace(/^0+/, "")}`;

  const rates = config?.shipping_rates || [];
  const discount = coupon?.discount || 0;
  const rate = rates.find((r) => r.id === shippingMethod) || rates[0];
  const threshold = config?.free_shipping_threshold || 0;
  const freeShip = coupon?.free_shipping || (threshold > 0 && subtotal - discount >= threshold);
  const shippingFee = freeShip ? 0 : (rate?.price ?? 0);
  const wrapFee = config?.gift_wrap_fee || 0;
  const itemWrapCount = items.reduce((s, i) => s + (i.gift_wrap ? 1 : 0), 0);
  const itemWrapFee = gift.wrap ? 0 : itemWrapCount * wrapFee; // order-level wrap covers all items
  const giftFee = (gift.wrap ? wrapFee : 0) + itemWrapFee;
  const shipGap = threshold > 0 && !freeShip ? threshold - (subtotal - discount) : 0;
  const preRewards = subtotal - discount + shippingFee + giftFee;
  const gstRate = config?.gst_rate ?? 3;
  const walletApplied = useWallet && wallet?.balance > 0 ? Math.min(wallet.balance, preRewards) : 0;
  const loyaltyPointsApplied = useLoyalty && loyalty?.balance >= (loyalty?.min_redeem || 100)
    ? Math.min(loyalty.balance, Math.max(0, (preRewards - walletApplied) / (loyalty?.redeem_ratio || 1))) : 0;
  const loyaltyRupee = loyaltyPointsApplied * (loyalty?.redeem_ratio || 1);
  const total = Math.max(0, preRewards - walletApplied - loyaltyRupee);

  const applyCoupon = async (codeOverride) => {
    const val = (codeOverride ?? code).trim();
    if (!val) return;
    try {
      const r = await api.post("/coupons/validate", { code: val, subtotal });
      setCoupon(r.data);
      setCode(r.data.code);
      toast.success(`Coupon ${r.data.code} applied`);
    } catch (e) { setCoupon(null); toast.error(errMsg(e)); }
  };

  const sendOtp = async (isResend = false) => {
    if (!/^\S+@\S+\.\S+$/.test(otpEmail)) { toast.error("Enter a valid email address"); return; }
    setOtpBusy(true);
    try {
      const r = await api.post("/auth/otp/request", { identifier: otpEmail });
      setOtpSent(true);
      setResendIn(30);
      if (isResend) setResendCount((c) => c + 1);
      if (r.data?.dev_otp) setDevOtp(r.data.dev_otp);
      toast.success(`Code sent to ${otpEmail}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setOtpBusy(false); }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otpCode)) { toast.error("Enter the 6-digit code"); return; }
    setOtpBusy(true);
    try {
      const u = await otpLogin(otpEmail, otpCode, otpName);
      toast.success(`Welcome, ${u.name}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setOtpBusy(false); }
  };

  const switchAccount = () => {
    logout();
    setOtpEmail(""); setOtpCode(""); setOtpName(""); setOtpSent(false); setDevOtp("");
    setUseWallet(false); setUseLoyalty(false);
    setAddresses([]);
    setForm({ name: "", phone: "", countryCode: "+91", pincode: "", line1: "", line2: "", city: "", state: "" });
  };

  const validate = () => {
    if (!user) return "Verify your email with the one-time code to continue";
    if (!form.name.trim()) return "Please enter your full name";
    if (!phoneValid) return `Enter a valid ${country.len}-digit mobile number (${country.code}) — the courier needs it`;
    if (!/^\d{6}$/.test(form.pincode)) return "Enter a valid 6-digit pincode";
    if (!form.line1.trim() || !form.city.trim() || !form.state) return "Please complete the address";
    return null;
  };

  const buildPayload = (acceptReprice) => ({
    items: items.map((i) => ({      product_id: i.product_id, variant_id: i.variant_id, qty: i.qty, options: i.options || {},
      locked_price: i.locked_price, locked_at: i.locked_at, rate_id: i.rate_id,
      personalisation: i.personalisation || null, design: i.design || "", size: i.size || "",
      gift_wrap: !!i.gift_wrap,
    })),
    customer: { name: form.name, email: user?.email || "", phone: phoneE164 },
    address: { label: "Home", name: form.name, phone: phoneE164, pincode: form.pincode, line1: form.line1, line2: form.line2, city: form.city, state: form.state },
    shipping_method: shippingMethod,
    payment_method: "razorpay",
    coupon_code: coupon?.code || "",
    accept_reprice: acceptReprice,
    gift,
    wallet_redeem: walletApplied,
    loyalty_redeem: loyaltyPointsApplied,
  });

  const verifyAndFinish = async (orderId, paymentId, signature) => {
    await api.post("/payments/verify", { order_id: orderId, razorpay_payment_id: paymentId, razorpay_signature: signature });
    clear();
    navigate(`/order-success/${orderId}`);
  };

  const placeOrder = async (acceptReprice = false) => {
    const err = validate();
    if (err) {
      toast.error(err);
      // Scroll to the FIRST invalid field, not the top — centred so neither the
      // sticky header nor the mobile tab bar covers it.
      const field = [[/email/i, "checkout-otp-email-input"], [/full name/i, "checkout-name-input"],
        [/mobile/i, "checkout-phone-input"], [/pincode/i, "checkout-pincode-input"],
        [/address/i, "checkout-address1-input"]].find(([re]) => re.test(err));
      const el = field && document.querySelector(`[data-testid="${field[1]}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus({ preventScroll: true }); }
      return;
    }
    emit("add_payment_info", { payment_type: "razorpay_upi", value: total }, { email: user?.email, phone: phoneE164 });
    setPlacing(true);
    try {
      const r = await api.post("/orders", buildPayload(acceptReprice));
      const { order_id, razorpay } = r.data;
      if (razorpay?.mock) { setMockPay({ order_id, razorpay }); return; }
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Could not load Razorpay checkout");
      const rzp = new window.Razorpay({
        key: razorpay.key_id, amount: razorpay.amount, currency: "INR",
        name: config?.store_name || "Siyaara", description: r.data.order_number,
        order_id: razorpay.order_id,
        prefill: { name: form.name, email: user?.email || "", contact: phoneE164 },
        config: {
          display: {
            blocks: { upi: { name: "Pay via UPI", instruments: [{ method: "upi" }] } },
            sequence: ["block.upi"],
            preferences: { show_default_blocks: true },
          },
        },
        handler: async (resp) => {
          try { await verifyAndFinish(order_id, resp.razorpay_payment_id, resp.razorpay_signature); }
          catch (e) { toast.error(errMsg(e)); }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled — your order is saved as unpaid") },
        theme: { color: "#372248" },
      });
      rzp.open();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "price_changed") {
        setPriceChanges(detail.changes);
      } else {
        toast.error(errMsg(e));
      }
    } finally {
      setPlacing(false);
    }
  };

  const acceptNewPrices = async () => {
    refreshPrices(priceChanges);
    setPriceChanges(null);
    await placeOrder(true);
  };

  const simulatePayment = async (success) => {
    if (!success) { setMockPay(null); toast.error("Mock payment failed — order saved as unpaid"); return; }
    try { await verifyAndFinish(mockPay.order_id, `pay_mock_${Date.now()}`, "mock_signature"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (items.length === 0 && !mockPay) {
    return (
      <div className="container-x py-24 text-center" data-testid="checkout-empty">
        <p className="text-stone-500">Your cart is empty.</p>
        <Button className="mt-4" onClick={() => navigate("/shop")} data-testid="checkout-shop-btn">Shop Jewellery</Button>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const addressDone = phoneValid && form.line1 && form.city && form.state && /^\d{6}$/.test(form.pincode);

  return (
    <div className="container-x py-8" data-testid="checkout-page">
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Checkout</h1>
      {/* Step indicator: email → address → delivery → payment */}
      <ol className="flex items-center gap-0 mt-5 mb-2 text-[11px] font-mono uppercase tracking-widest" data-testid="checkout-steps">
        {["Email", "Address", "Delivery", "Payment"].map((s, i) => {
          const done =
            (i === 0 && !!user) ||
            (i === 1 && !!user && addressDone) ||
            (i === 2 && !!user && addressDone && shippingMethod) ||
            (i === 3 && !!user && addressDone);
          return (
            <li key={s} className="flex items-center" data-testid={`checkout-step-${s.toLowerCase()}`}>
              <span className={`flex items-center gap-1.5 ${done ? "text-brand" : "text-stone-400"}`}>
                <span className={`h-5 w-5 rounded-full border flex items-center justify-center ${done ? "border-brand bg-brand text-ivory" : "border-stone-300"}`}>{i + 1}</span>
                <span className="hidden sm:inline">{s}</span>
              </span>
              {i < 3 && <span className={`w-8 sm:w-14 h-px mx-2 ${done ? "bg-brand" : "bg-stone-300"}`} />}
            </li>
          );
        })}
      </ol>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-8">
        <div className="lg:col-span-3 space-y-6">
          {/* STEP 1 — email-first OTP */}
          <section className="border border-stone-200 bg-white p-6" data-testid="checkout-email-step">
            <h2 className="font-heading font-semibold text-lg">1. Your Email</h2>
            {user ? (
              <div className="flex items-center justify-between mt-4 border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="checkout-email-verified">
                <span className="flex items-center gap-2 text-sm text-emerald-900">
                  <MailCheck size={18} className="text-emerald-700" />
                  Verified: <strong className="font-mono">{user.email}</strong>
                </span>
                <button onClick={switchAccount} className="text-xs underline text-stone-500 hover:text-stone-900" data-testid="checkout-switch-account-btn">Use a different email</button>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-stone-500">We email you a one-time code — no passwords, no spam. Your account is created automatically.</p>
                <div className="flex gap-2">
                  <Input type="email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value.trim().toLowerCase())}
                    placeholder="you@example.com" className="h-11" disabled={otpSent}
                    data-testid="checkout-otp-email-input" />
                  {!otpSent && (
                    <Button onClick={() => sendOtp(false)} disabled={otpBusy} className="h-11 bg-stone-900 hover:bg-brand text-white btn-press" data-testid="checkout-otp-send-btn">
                      {otpBusy ? "Sending…" : "Email me a code"}
                    </Button>
                  )}
                </div>
                {otpSent && (
                  <div className="space-y-3 border-t border-stone-100 pt-4">
                    <div><Label htmlFor="co-otp">Enter the 6-digit code</Label>
                      <Input id="co-otp" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="6-digit code" className="h-11 font-mono tracking-[0.5em]" data-testid="checkout-otp-code-input" />
                      <p className="text-[11px] text-stone-500 mt-1">Or tap the <strong>sign-in link</strong> in the same email — no typing needed.</p>
                      {devOtp && <p className="text-[11px] font-mono text-amber-700 mt-1" data-testid="checkout-otp-dev-hint">TEST MODE — your code is {devOtp}</p>}
                      {resendCount > 0 && <p className="text-[11px] text-stone-500 mt-1" data-testid="checkout-spam-hint">Not seeing it? Check your spam/junk folder.</p>}
                    </div>
                    <div><Label htmlFor="co-otp-name">Your Name</Label>
                      <Input id="co-otp-name" value={otpName} onChange={(e) => setOtpName(e.target.value)} data-testid="checkout-otp-name-input" />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={verifyOtp} disabled={otpBusy} className="flex-1 h-11 bg-stone-900 hover:bg-brand text-white btn-press" data-testid="checkout-otp-verify-btn">
                        {otpBusy ? "Verifying…" : "Verify & Continue"}
                      </Button>
                      <Button variant="outline" onClick={() => sendOtp(true)} disabled={otpBusy || resendIn > 0} className="h-11" data-testid="checkout-otp-resend-btn">
                        {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* STEP 2 — address, revealed only after email verification */}
          {user ? (
            <section className="border border-stone-200 bg-white p-6" data-testid="checkout-address-step">
              <h2 className="font-heading font-semibold text-lg">2. Delivery Address</h2>
              {addresses.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {addresses.map((a) => (
                    <button key={a.id} onClick={() => setForm((f) => ({ ...f, ...pick(a) }))} className="text-xs border border-stone-200 px-3 py-1.5 hover:border-stone-900 font-mono" data-testid={`saved-address-${a.id}`}>
                      {a.label}: {a.line1}, {a.city} {a.pincode}
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div><Label htmlFor="co-name">Full Name</Label><Input id="co-name" value={form.name} onChange={set("name")} data-testid="checkout-name-input" /></div>
                <div>
                  <Label htmlFor="co-phone">Mobile (courier needs this)</Label>
                  <div className="flex gap-2">
                    <select value={form.countryCode} onChange={(e) => setForm((f) => ({ ...f, countryCode: e.target.value, phone: "" }))}
                      className="h-10 w-28 border border-stone-200 rounded-sm px-2 text-sm bg-white shrink-0" data-testid="checkout-country-select">
                      {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    <Input id="co-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, country.len) }))}
                      placeholder={`${country.len}-digit mobile`} className="h-10 font-mono" data-testid="checkout-phone-input" />
                  </div>
                  {form.phone && !phoneValid && <p className="text-[11px] text-amber-700 font-mono mt-1" data-testid="phone-invalid-hint">Enter a valid {country.len}-digit number for {country.code}</p>}
                </div>
                <div>
                  <Label htmlFor="co-pincode">PIN Code</Label>
                  <Input id="co-pincode" value={form.pincode} onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))} data-testid="checkout-pincode-input" />
                  {pinLookup === "loading" && <p className="text-[11px] text-stone-400 font-mono mt-1">Looking up city & state…</p>}
                  {pinLookup === "ok" && <p className="text-[11px] text-emerald-700 font-mono mt-1" data-testid="pincode-autofill-ok">City & state auto-filled</p>}
                  {pinLookup === "fail" && <p className="text-[11px] text-amber-700 font-mono mt-1" data-testid="pincode-autofill-fail">Couldn't look up this PIN — enter city & state manually</p>}
                </div>
                <div className="sm:col-span-2"><Label htmlFor="co-line1">Address Line 1</Label><Input id="co-line1" value={form.line1} onChange={set("line1")} placeholder="Flat / House no., Street" data-testid="checkout-address1-input" /></div>
                <div className="sm:col-span-2"><Label htmlFor="co-line2">Address Line 2 (optional)</Label><Input id="co-line2" value={form.line2} onChange={set("line2")} placeholder="Landmark, Area" data-testid="checkout-address2-input" /></div>
                <div><Label htmlFor="co-city">City</Label><Input id="co-city" value={form.city} onChange={set("city")} data-testid="checkout-city-input" /></div>
                <div>
                  <Label htmlFor="co-state">State</Label>
                  <select id="co-state" value={form.state} onChange={set("state")} className="w-full h-10 border border-stone-200 rounded-sm px-3 text-sm bg-white" data-testid="checkout-state-select">
                    <option value="">Select state</option>
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </section>
          ) : (
            <section className="border border-dashed border-stone-200 bg-stone-50 p-6 text-sm text-stone-400" data-testid="checkout-address-locked">
              2. Delivery Address — verify your email above to continue
            </section>
          )}

          {user && (
            <>
              <section className="border border-stone-200 bg-white p-6">
                <h2 className="font-heading font-semibold text-lg">3. Shipping</h2>
                <div className="space-y-2 mt-4">
                  {rates.map((r) => (
                    <label key={r.id} className={`flex items-center justify-between border p-4 cursor-pointer transition-colors ${shippingMethod === r.id ? "border-stone-900 bg-stone-50" : "border-stone-200"}`} data-testid={`shipping-${r.id}`}>
                      <span className="flex items-center gap-3">
                        <input type="radio" name="shipping" checked={shippingMethod === r.id} onChange={() => setShippingMethod(r.id)} />
                        <span>
                          <span className="text-sm font-medium block">{r.name}</span>
                          <span className="text-xs text-stone-500 font-mono">{r.eta} · fully insured</span>
                        </span>
                      </span>
                      <span className="font-heading font-semibold text-sm">{freeShip || !r.price ? "FREE" : inr(r.price)}</span>
                    </label>
                  ))}
                </div>
              </section>

              <section className="border border-stone-200 bg-white p-6" data-testid="gift-section">
                <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Gift size={18} className="text-brand" /> Gifting</h2>
                <div className="space-y-3 mt-4">
                  <div className="flex items-center gap-4">
                    {config?.brand_imagery?.gift_wrap && (
                      <img src={config.brand_imagery.gift_wrap} alt="Signature gift wrap — aubergine box, gold ribbon"
                        className="h-16 w-16 rounded-lg object-cover border border-stone-200" data-testid="gift-wrap-image" />
                    )}
                    <label className="flex items-center gap-3 text-sm">
                      <Switch checked={gift.wrap} onCheckedChange={(v) => setGift({ ...gift, wrap: v })} data-testid="gift-wrap-toggle" />
                      Signature gift wrap {config?.gift_wrap_fee ? `(+${inr(config.gift_wrap_fee)})` : ""}
                    </label>
                  </div>
                  {gift.wrap && (
                    <div>
                      <Label>Gift message</Label>
                      <textarea value={gift.message} maxLength={120} onChange={(e) => setGift({ ...gift, message: e.target.value })} placeholder="A note for the lucky one…" className="w-full h-16 border border-stone-200 rounded-sm px-3 py-2 text-sm mt-1" data-testid="gift-message-input" />
                    </div>
                  )}
                  <label className="flex items-center gap-3 text-sm">
                    <Switch checked={gift.hide_price} onCheckedChange={(v) => setGift({ ...gift, hide_price: v })} data-testid="gift-hide-price-toggle" />
                    Hide prices on the invoice (for gifts)
                  </label>
                </div>
              </section>

              <section className="border border-stone-200 bg-white p-6">
                <h2 className="font-heading font-semibold text-lg">4. Payment</h2>
                {hasMto && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 mt-3" data-testid="checkout-mto-notice">
                    This order includes a <strong>made-to-order</strong> piece{mtoLead ? ` — crafted for you in ${mtoLead}` : ""}. Full payment is taken now; free cancellation applies only before metal procurement begins.
                  </p>
                )}
                <div className="space-y-2 mt-4">
                  <label className="flex items-center justify-between border border-stone-900 bg-stone-50 p-4" data-testid="payment-razorpay">
                    <span className="flex items-center gap-3">
                      <input type="radio" name="payment" checked readOnly />
                      <span>
                        <span className="text-sm font-medium block">Pay Online — UPI first</span>
                        <span className="text-xs text-stone-500">UPI · Cards · NetBanking · Wallets via Razorpay{config?.razorpay_mode === "mock" ? " (test mode)" : ""}</span>
                      </span>
                    </span>
                    <ShieldCheck size={18} className="text-emerald-700" />
                  </label>
                  <p className="text-xs text-stone-500 font-mono" data-testid="prepaid-only-note">Prepaid only — we do not offer Cash on Delivery. Every shipment is fully insured.</p>
                </div>
                {hasPersonalised && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 mt-4" data-testid="checkout-personalised-notice">
                    This order includes a personalised piece — made to order and <strong>non-returnable</strong>.
                  </p>
                )}
              </section>
            </>
          )}
        </div>

        <aside className="lg:col-span-2 border border-stone-200 bg-white p-6 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
          <h2 className="font-heading font-semibold text-lg">Order Summary</h2>
          <ul className="mt-4 space-y-3 max-h-56 overflow-y-auto">
            {items.map((i) => (
              <li key={i.key} className="flex gap-3 text-sm">
                {i.image
                  ? <img src={i.image} alt="" className="h-12 w-12 object-cover bg-stone-50 border border-stone-200" />
                  : <div className="h-12 w-12 shrink-0 bg-[#FBF8F3] border border-stone-200 flex items-center justify-center" aria-hidden="true"><span className="font-heading text-brand/40 text-sm">S</span></div>}
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{i.title}</p>
                  <p className="text-xs text-stone-500 font-mono">{i.variant_label} × {i.qty}</p>
                  {i.tier === "made_to_order" && (
                    <p className="text-[11px] text-amber-700 font-medium" data-testid="summary-mto">Made to order{i.lead_time ? ` — ${i.lead_time}` : ""}</p>
                  )}
                  {i.personalisation?.summary && <p className="text-[11px] text-brand truncate">✦ {i.personalisation.summary}</p>}
                </div>
                <span className="font-medium">{inr(i.locked_price * i.qty)}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 mt-4">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="h-10 font-mono" data-testid="checkout-coupon-input" />
            <Button variant="outline" onClick={() => applyCoupon()} className="h-10 btn-press" data-testid="checkout-coupon-apply">Apply</Button>
          </div>
          <dl className="mt-4 space-y-2 text-sm border-t border-stone-200 pt-4">
            <div className="flex justify-between"><dt className="text-stone-600">Subtotal</dt><dd>{inr(subtotal)}</dd></div>
            {discount > 0 && <div className="flex justify-between text-emerald-700"><dt>Discount ({coupon?.code})</dt><dd>−{inr(discount)}</dd></div>}
            <div className="flex justify-between"><dt className="text-stone-600">Shipping</dt><dd data-testid="checkout-shipping">{shippingFee === 0 ? "FREE" : inr(shippingFee)}</dd></div>
            {giftFee > 0 && <div className="flex justify-between"><dt className="text-stone-600">Gift wrap{!gift.wrap && itemWrapCount ? ` (${itemWrapCount} item${itemWrapCount > 1 ? "s" : ""})` : ""}</dt><dd>{inr(giftFee)}</dd></div>}
            {walletApplied > 0 && <div className="flex justify-between text-emerald-700" data-testid="checkout-wallet-line"><dt>Store credit</dt><dd>−{inr(walletApplied)}</dd></div>}
            {loyaltyRupee > 0 && <div className="flex justify-between text-emerald-700" data-testid="checkout-loyalty-line"><dt>Loyalty points ({Math.round(loyaltyPointsApplied).toLocaleString("en-IN")} pts)</dt><dd>−{inr(loyaltyRupee)}</dd></div>}
            <div className="flex justify-between text-xs text-stone-500"><dt>Prices include GST @ {gstRate}%</dt></div>
          </dl>
          <div className="border-t border-stone-200 mt-3 pt-3 flex justify-between font-heading font-semibold text-lg">
            <span>Total</span><span data-testid="checkout-total">{inr(total)}</span>
          </div>
          {shipGap > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 mt-3" data-testid="checkout-freeship-gap">
              Add <strong>{inr(shipGap)}</strong> more for FREE insured shipping
            </p>
          )}
          {discount > 0 && (
            <p className="text-xs text-emerald-700 mt-3" data-testid="checkout-savings">You're saving {inr(discount)} with {coupon?.code}</p>
          )}
          {user && (wallet?.balance > 0 || loyalty?.balance > 0) && (
            <div className="mt-3 border border-gold/40 bg-ivory p-3 space-y-2" data-testid="checkout-rewards">
              <p className="text-[10px] font-mono uppercase tracking-widest text-brand">Your rewards</p>
              {wallet?.balance > 0 && (
                <label className="flex items-center justify-between text-sm cursor-pointer" data-testid="checkout-wallet-toggle">
                  <span className="flex items-center gap-2">
                    <Switch checked={useWallet} onCheckedChange={setUseWallet} />
                    Store credit <span className="font-mono text-xs text-stone-500">({inr(wallet.balance)} available)</span>
                  </span>
                  {useWallet && <span className="font-medium text-emerald-700">−{inr(walletApplied)}</span>}
                </label>
              )}
              {loyalty?.balance > 0 && (
                <label className="flex items-center justify-between text-sm cursor-pointer" data-testid="checkout-loyalty-toggle">
                  <span className="flex items-center gap-2">
                    <Switch checked={useLoyalty} onCheckedChange={setUseLoyalty}
                      disabled={loyalty.balance < (loyalty.min_redeem || 100)} />
                    Loyalty points <span className="font-mono text-xs text-stone-500">({loyalty.balance.toLocaleString("en-IN")} pts)</span>
                  </span>
                  {useLoyalty && loyaltyPointsApplied > 0 && <span className="font-medium text-emerald-700">−{inr(loyaltyRupee)}</span>}
                </label>
              )}
              {loyalty?.balance > 0 && loyalty.balance < (loyalty.min_redeem || 100) && (
                <p className="text-[11px] text-stone-400">Minimum {(loyalty.min_redeem || 100).toLocaleString("en-IN")} pts to redeem</p>
              )}
            </div>
          )}
          <Button className="w-full mt-5 h-12 bg-stone-900 hover:bg-brand text-white btn-press text-base rounded-none" onClick={() => placeOrder(false)} disabled={placing || !user} data-testid="place-order-btn">
            {placing ? "Placing order…" : !user ? "Verify email to pay" : `Pay ${inr(total)}`}
          </Button>
          <div className="flex items-center justify-center gap-3 mt-3 text-[10px] font-mono text-stone-400" data-testid="checkout-trust-strip">
            <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-emerald-700" />SECURE PAYMENT</span>
            <span>BIS HALLMARKED</span>
            <span>FULLY INSURED</span>
          </div>
          {!user && (
            <p className="text-xs text-stone-500 mt-3 text-center" data-testid="checkout-verify-hint">
              Verify your email above — your account is created automatically with the one-time code.
            </p>
          )}
        </aside>
      </div>

      {/* Exit-intent offer (one-shot per session) */}
      <Dialog open={!!exitOffer} onOpenChange={() => setExitOffer(null)}>
        <DialogContent className="sm:max-w-sm" aria-describedby={undefined} data-testid="exit-offer-dialog">
          <DialogHeader><DialogTitle className="font-heading">Wait — one thing before you go</DialogTitle></DialogHeader>
          {exitOffer && (
            <p className="text-sm text-stone-600">
              Use <strong className="font-mono">{exitOffer.code}</strong> at checkout:{" "}
              {exitOffer.type === "percentage" ? `${exitOffer.value}% off` : exitOffer.type === "flat" ? `${inr(exitOffer.value)} off` : "free insured shipping"}
              {exitOffer.min_order ? ` on orders above ${inr(exitOffer.min_order)}` : ""}.
            </p>
          )}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button className="bg-stone-900 hover:bg-brand text-white btn-press" onClick={() => { applyCoupon(exitOffer?.code); setExitOffer(null); }} data-testid="exit-offer-apply-btn">Apply Offer</Button>
            <Button variant="outline" onClick={() => setExitOffer(null)} data-testid="exit-offer-dismiss-btn">No Thanks</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mock Razorpay dialog */}
      <Dialog open={!!mockPay} onOpenChange={() => setMockPay(null)}>
        <DialogContent className="sm:max-w-sm" aria-describedby={undefined} data-testid="mock-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Razorpay — Test Mode</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">
            No live Razorpay keys configured, so this is a simulated payment for{" "}
            <span className="font-semibold">{mockPay ? inr(mockPay.razorpay.amount / 100) : ""}</span>.
            Add keys to <code className="font-mono text-xs">.env</code> to go live.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white btn-press" onClick={() => simulatePayment(true)} data-testid="mock-pay-success-btn">Simulate Success</Button>
            <Button variant="outline" onClick={() => simulatePayment(false)} data-testid="mock-pay-fail-btn">Simulate Failure</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price-change (rate moved) dialog */}
      <Dialog open={!!priceChanges} onOpenChange={() => setPriceChanges(null)}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined} data-testid="reprice-dialog">
          <DialogHeader><DialogTitle className="font-heading">Metal rates have moved</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">
            Gold/silver rates changed while you were shopping. Your locked prices have expired — please review the updated prices before paying. We never change prices silently.
          </p>
          <ul className="space-y-2 mt-2">
            {(priceChanges || []).map((c) => (
              <li key={c.variant_id} className="flex justify-between text-sm border border-stone-200 px-3 py-2" data-testid={`reprice-${c.variant_id}`}>
                <span className="font-medium truncate pr-3">{c.title}</span>
                <span className="font-mono shrink-0"><span className="line-through text-stone-400 mr-2">{inr(c.old)}</span>{inr(c.new)}</span>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button className="bg-stone-900 hover:bg-brand text-white btn-press" onClick={acceptNewPrices} data-testid="accept-reprice-btn">Accept & Continue</Button>
            <Button variant="outline" onClick={() => setPriceChanges(null)} data-testid="cancel-reprice-btn">Review Cart</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
